import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { filter, take } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { FirestoreConnectionService } from '../../services/firestore-connection.service';
import { ApiService } from '../../services/api.service';
import { ReferenzeService } from '../../services/referenze.service';
import { ReferenzaFirestore } from '../../core/models/firestore/referenza.model';
import { environment } from '../../../environments/environment';
import { combineLatest, Subscription, firstValueFrom } from 'rxjs';
import { TagService } from '../../services/tag.service';
import { TagModel } from '../../core/models/api/tag.model';
import { ExcelExportService } from '../../services/excel-export.service';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { ReferenzaModel } from '../../core/models/api/referenza.model';
import { RuoloProfilo } from '../../core/models/firestore/referenza.model';
import { RuoloReferenzaModel } from '../../core/models/api/ruolo-referenza.model';
import { ProfiloModel } from '../../core/models/api/profilo.model';
import { UserRoleService } from '../../services/user-role.service';
import { RuoliReferenzaService } from '../../services/ruoli-referenza.service';
import { ProfiliService } from '../../services/profili.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchShareService } from '../../services/search-share.service';
import { Menu } from 'primeng/menu';
import { MultiSelect } from 'primeng/multiselect';
import { CATEGORIE_SERVIZI } from '../../core/constants/categorie-servizi.constants';

@Component({
  selector: 'app-home',
  standalone: false,
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  private referenzeSub?: Subscription;
  referenze: ReferenzaFirestore[] = [];
  viewMode: 'table' | 'grid' | 'map' = 'table';
  filterText = '';

  // Filtri strutturati (dropdown)
  selectedClienti: string[] = [];
  selectedProjectManagers: string[] = [];
  selectedCategorieServizio: string[] = [];
  selectedCommessa: string = ''; // Singolo codice commessa (es: "C001")
  selectedCertificato: string = ''; // Singolo codice certificato

  clienteOptions: Array<{ label: string; value: string }> = [];
  projectManagerOptions: Array<{ label: string; value: string }> = [];
  categorieServizioOptions: Array<{ label: string; value: string }> = [];
  commessaOptions: Array<{ label: string; value: string }> = [];
  certificatoOptions: Array<{ label: string; value: string }> = [];

  // Filtri ruoli assegnati
  selectedRuoliFilter: string[] = []; // valori = ruoloCodice
  selectedPersoneFilter: number[] = []; // valori = profiloId
  ruoliFilterOptions: Array<{ label: string; value: string; codice: string }> =
    [];
  personeFilterOptions: Array<{ label: string; value: number }> = [];
  @ViewChild('ruoliFilter') ruoliFilter?: MultiSelect;
  @ViewChild('personeFilter') personeFilter?: MultiSelect;

  // Filtri tag/BIM/servizi accessori
  tagsByCategory: Record<string, TagModel[]> = {};
  selectedTagsByCategory: Record<string, string[]> = {};
  bimOnly = false;
  serviziAccessoriOnly = false;
  // Disabilita la casella di ricerca nei dropdown dei tag (temporaneamente)
  enableTagSearch = false;
  // Configurazione categorie tag (nuovo modello a 5 categorie)
  private readonly tagCategoryOrder: string[] = [
    'FASI',
    'TEMATICHE',
    'TIPOLOGIA',
    'PRESTAZIONI',
    'TIPO_STRUTTURE',
  ];
  private readonly tagCategoryIcons: Record<string, string> = {
    FASI: 'timeline',
    TEMATICHE: 'schema',
    TIPOLOGIA: 'category',
    PRESTAZIONI: 'speed',
    TIPO_STRUTTURE: 'apartment',
  };
  groupedCategories: Array<{
    groupName: string;
    groupLabel: string;
    groupIcon: string;
    categories: Array<{ key: string; value: TagModel[] }>;
  }> = [];
  /** L'avviso di backend non raggiungibile va mostrato una volta sola. */
  private avvisoBackendMostrato = false;

  allTags: TagModel[] = [];

  // Dialog creazione tag (in Home)
  showCreateTagDialog = false;
  createTagInitialCategory: string | null = null;

  // Wizard nuova referenza
  showNuovaReferenzaWizard = false;
  referenzaToEdit: ReferenzaFirestore | null = null;

  // Dialog tag referenza
  showTagsDialog = false;
  selectedReferenzaForTags: ReferenzaFirestore | null = null;

  // Dialog di condivisione
  showShareDialog = false;

  // Dialog help filtri
  showHelpDialog = false;

  // Dialog ruoli referenza
  showRuoliDialog = false;
  selectedReferenzaForRuoli: ReferenzaFirestore | null = null;
  ruoliDisponibili: RuoloReferenzaModel[] = [];
  profiliDisponibili: ProfiloModel[] = [];
  loadingRuoliDialog = false;
  savingRuolo = false;
  newRuoloId: number | null = null;
  newProfiloId: number | null = null;
  newFirmatario = false;
  showCreateProfiloDialog = false;
  savingNewProfilo = false;
  newProfiloNome = '';
  newProfiloCognome = '';

  // Dialog categorie servizio referenza
  showCategoriServizioDialog = false;
  selectedReferenzaForCategoriServizio: ReferenzaFirestore | null = null;
  newCategoriaServizio: string | null = null;
  savingCategoriaServizio = false;
  readonly tutteLeCategoriServizio = CATEGORIE_SERVIZI;

  // Dialog posizione referenza
  showPosizioneDialog = false;
  selectedReferenzaForPosizione: ReferenzaFirestore | null = null;

  // Dialog servizi accessori
  showServiziAccessoriDialog = false;
  selectedReferenzaForServiziAccessori: ReferenzaFirestore | null = null;
  serviziAccessoriUrl = '';
  savingServiziAccessori = false;

  constructor(
    private auth: AuthService,
    private logger: NGXLogger,
    private firestoreConnectionService: FirestoreConnectionService,
    private apiService: ApiService,
    private referenzeService: ReferenzeService,
    private tagService: TagService,
    private excelExportService: ExcelExportService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private userRoleService: UserRoleService,
    private ruoliReferenzaService: RuoliReferenzaService,
    private profiliService: ProfiliService,
    private route: ActivatedRoute,
    private router: Router,
    private searchShareService: SearchShareService,
  ) {}

  /**
   * Verifica se l'utente può modificare (non è VIEWER)
   */
  canModify(): boolean {
    return this.userRoleService.canModify();
  }

  /**
   * Verifica se l'utente è VIEWER
   */
  isViewer(): boolean {
    return this.userRoleService.isViewer();
  }

  /**
   * Normalizza una stringa rimuovendo accenti e caratteri speciali per il matching
   * Converte spazi in underscore e tutto in maiuscolo per matchare il formato delle referenze
   */
  private normalizeString(str: string): string {
    if (!str) return '';
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Rimuove i diacritici (accenti)
      .replace(/[''""]/g, "'") // Normalizza apostrofi
      .replace(/\s+/g, '_') // Converte spazi in underscore (come nel formato referenze)
      .trim() // Rimuove spazi iniziali/finali
      .toUpperCase();
  }

  /**
   * Converte il codice del tag dal formato DB (minuscolo con spazi)
   * al formato referenze (maiuscolo con underscore)
   * Es: "indagini archeologiche" -> "INDAGINI_ARCHEOLOGICHE"
   */
  private convertCodiceToReferenzeFormat(codice: string): string {
    if (!codice) return '';
    return codice
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Rimuove accenti
      .replace(/[''""]/g, "'") // Normalizza apostrofi
      .replace(/\s+/g, '_') // Converte spazi in underscore
      .trim()
      .toUpperCase();
  }

  get filteredReferenze(): ReferenzaFirestore[] {
    const term = this.filterText.trim().toLowerCase();

    const activeCats = Object.keys(this.selectedTagsByCategory).filter(
      (c) => (this.selectedTagsByCategory[c] || []).length > 0,
    );

    /*  this.logger.debug('[HOME] Filtro referenze - Parametri:', {
      term: term || '(vuoto)',
      bimOnly: this.bimOnly,
      activeCategories: activeCats,
      selectedTagsByCategory: this.selectedTagsByCategory,
      totalReferenze: this.referenze.length,
    }); */

    const filtered = this.referenze.filter((ref) => {
      // Filtro BIM: usa il campo bim della referenza
      if (this.bimOnly) {
        // Se bimOnly è true, mostra solo referenze con bim === true
        // Se bim è undefined/null, viene trattato come false
        if (ref.bim !== true) {
          /* this.logger.debug(
            '[HOME] Referenza esclusa - BIM filter:',
            ref.titoloBreve,
            'bim:',
            ref.bim,
          ); */
          return false;
        }
      }

      // Filtro servizi accessori
      if (this.serviziAccessoriOnly) {
        if (ref.serviziAccessoriPresenti !== true) {
          return false;
        }
      }

      // Filtri strutturati (dropdown)
      if ((this.selectedClienti?.length ?? 0) > 0) {
        const cliente = (ref.cliente || '').trim();
        if (!cliente || !this.selectedClienti.includes(cliente)) {
          return false;
        }
      }

      if ((this.selectedProjectManagers?.length ?? 0) > 0) {
        const pm = (ref.projectManager || '').trim();
        if (!pm || !this.selectedProjectManagers.includes(pm)) {
          return false;
        }
      }

      if ((this.selectedCategorieServizio?.length ?? 0) > 0) {
        const cats = ref.categorieServizio || [];
        const hasAny = cats.some((c) =>
          this.selectedCategorieServizio.includes(c),
        );
        if (!hasAny) {
          return false;
        }
      }

      // Filtri ruoli/persone: AND all'interno di ciascuna select;
      // se entrambi attivi, ogni persona/ruolo deve combaciare sulle assegnazioni compatibili.
      const hasRuoliFilter = (this.selectedRuoliFilter?.length ?? 0) > 0;
      const hasPersoneFilter = (this.selectedPersoneFilter?.length ?? 0) > 0;
      if (hasRuoliFilter || hasPersoneFilter) {
        const rps = ref.ruoliProfili ?? [];

        if (hasPersoneFilter) {
          const allPersoneOk = this.selectedPersoneFilter.every((profiloId) =>
            rps.some(
              (rp) =>
                rp.profiloId === profiloId &&
                (!hasRuoliFilter ||
                  this.selectedRuoliFilter.includes(rp.ruoloCodice)),
            ),
          );
          if (!allPersoneOk) return false;
        }

        if (hasRuoliFilter) {
          const allRuoliOk = this.selectedRuoliFilter.every((ruoloCodice) =>
            rps.some(
              (rp) =>
                rp.ruoloCodice === ruoloCodice &&
                (!hasPersoneFilter ||
                  this.selectedPersoneFilter.includes(rp.profiloId)),
            ),
          );
          if (!allRuoliOk) return false;
        }
      }

      // Filtro per commessa (codiceCommerciale)
      if ((this.selectedCommessa ?? '').trim()) {
        const codiceCommerciale = ref.codiceCommerciale;
        if (codiceCommerciale === null || codiceCommerciale === undefined) {
          return false;
        }
        // Converti codiceCommerciale a stringa per il confronto
        const codiceStr =
          typeof codiceCommerciale === 'string'
            ? codiceCommerciale
            : codiceCommerciale.toString();

        if (codiceStr !== this.selectedCommessa) {
          return false;
        }
      }

      // Filtro per certificato
      if ((this.selectedCertificato ?? '').trim()) {
        const certificati = this.getCertificati(ref);
        if (!certificati.includes(this.selectedCertificato)) {
          return false;
        }
      }

      //this.logger.info(ref.tag);
      // Filtro per categorie di tag: richiede che TUTTE le categorie attive siano soddisfatte
      if (activeCats.length) {
        const refTags = ref.tag || []; // es: ["SETTORE#SANITA", "TIPOLOGIA#SCUOLA"]

        /*  this.logger.debug(
          `[HOME] Referenza "${ref.titoloBreve}" - Tag referenza:`,
          refTags,
        ); */

        const matchesAll = activeCats.every((cat) => {
          const selectedCodes = this.selectedTagsByCategory[cat] || [];
          if (!selectedCodes.length) {
            return true;
          }

          const matched = selectedCodes.some((code) => {
            // Converte il codice dal formato DB al formato referenze (maiuscolo + underscore)
            const codiceFormatoReferenze =
              this.convertCodiceToReferenzeFormat(code);
            const expectedTag = `${cat}#${codiceFormatoReferenze}`;
            // Normalizza entrambi i tag per gestire accenti e caratteri speciali
            const normalizedExpected = this.normalizeString(expectedTag);
            const found = refTags.some((refTag) => {
              const normalizedRefTag = this.normalizeString(refTag);
              return normalizedRefTag === normalizedExpected;
            });
            if (found) {
              this.logger.debug(
                `[HOME] Tag match trovato: ${expectedTag} (codice originale: ${code}, normalizzato: ${normalizedExpected}) in referenza "${ref.titoloBreve}"`,
              );
            }
            return found;
          });

          /* if (!matched) {
            this.logger.debug(
              `[HOME] Referenza "${ref.titoloBreve}" esclusa - categoria ${cat} non matcha. Cercati: ${selectedCodes.map((c) => `${cat}#${c}`).join(', ')}`,
            );
          } */

          return matched;
        });

        if (!matchesAll) {
          return false;
        }
      }

      // Filtro testo libero
      if (!term) {
        return true;
      }

      const testo = [
        ref.titoloBreve,
        ref.titoloEsteso,
        ref.cliente,
        ref.projectManager,
        ref.codiceCommerciale != null ? String(ref.codiceCommerciale) : null,
        ref.categorieServizio?.join(' '),
        ref.tag?.join(' '),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesText = testo.includes(term);
      /* if (!matchesText) {
        this.logger.debug(
          `[HOME] Referenza "${ref.titoloBreve}" esclusa - testo "${term}" non trovato`,
        );
      } */

      return matchesText;
    });

    // Mostra il log solo se ci sono filtri attivi
    const hasActiveFilters =
      term || this.bimOnly || this.serviziAccessoriOnly || activeCats.length > 0;
    if (hasActiveFilters) {
      /* this.logger.info('[HOME] Filtro completato:', {
        referenzeIniziali: this.referenze.length,
        referenzeFiltrate: filtered.length,
        filtriAttivi: {
          testo: term || null,
          bim: this.bimOnly,
          categorieTag: activeCats.length > 0 ? this.selectedTagsByCategory : null
        }
      }); */
    }

    return filtered;
  }

  get filteredReferenzeWithPosizione(): ReferenzaFirestore[] {
    return this.filteredReferenze.filter((r) => !!r.posizione);
  }

  get mapMarkers(): Array<{
    id: number;
    position: { lat: number; lng: number };
    popupHtml: string;
  }> {
    return this.filteredReferenzeWithPosizione.map((r) => {
      const pos = r.posizione!;
      const cliente = (r.cliente || 'N/D').toString();
      const commessa =
        r.codiceCommerciale !== null && r.codiceCommerciale !== undefined
          ? r.codiceCommerciale.toString()
          : 'N/D';

      const popupHtml = `
        <div style="display:flex; flex-direction:column; gap: 0.25rem;">
          <div style="font-weight: 800;">${this.escapeHtml(r.titoloBreve)}</div>
          <div style="font-size: 12px; color: #555;">
            <div><strong>Cliente:</strong> ${this.escapeHtml(cliente)}</div>
            <div><strong>Commessa:</strong> ${this.escapeHtml(commessa)}</div>
          </div>
        </div>
      `.trim();

      return {
        id: r.id,
        position: { lat: pos.latitude, lng: pos.longitude },
        popupHtml,
      };
    });
  }

  get mapMissingPosizioneCount(): number {
    return (
      this.filteredReferenze.length - this.filteredReferenzeWithPosizione.length
    );
  }

  openPosizioneDialog(ref: ReferenzaFirestore): void {
    this.selectedReferenzaForPosizione = ref;
    this.showPosizioneDialog = true;
  }

  onPosizioneSaved(): void {
    // La home si aggiorna automaticamente via Firestore listener.
  }

  openServiziAccessoriDialog(ref: ReferenzaFirestore): void {
    this.selectedReferenzaForServiziAccessori = ref;
    this.serviziAccessoriUrl = ref.urlServiziAccessori ?? '';
    this.showServiziAccessoriDialog = true;
  }

  closeServiziAccessoriDialog(): void {
    this.showServiziAccessoriDialog = false;
    this.selectedReferenzaForServiziAccessori = null;
    this.serviziAccessoriUrl = '';
  }

  canSaveServiziAccessori(): boolean {
    return this.serviziAccessoriUrl.trim().length > 0;
  }

  async saveServiziAccessori(): Promise<void> {
    const ref = this.selectedReferenzaForServiziAccessori;
    const url = this.serviziAccessoriUrl.trim();
    if (!ref || !url) return;

    this.savingServiziAccessori = true;
    try {
      await firstValueFrom(
        this.apiService.updateServiziAccessori(ref.id, {
          serviziAccessoriPresenti: true,
          urlServiziAccessori: url,
        }),
      );

      this.messageService.add({
        severity: 'success',
        summary:
          ref.serviziAccessoriPresenti === true
            ? 'Salvato'
            : 'Servizi affini attivati',
        detail: 'Servizi affini e URL salvati correttamente.',
        life: 3000,
      });
      this.closeServiziAccessoriDialog();
    } catch (error: unknown) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: this.getServiziAccessoriErrorMessage(error),
        life: 5000,
      });
    } finally {
      this.savingServiziAccessori = false;
    }
  }

  private getServiziAccessoriErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const apiMessage =
        typeof error.error === 'string'
          ? error.error
          : error.error?.message || error.error?.detail;
      if (apiMessage) {
        return apiMessage;
      }
      if (error.status === 0) {
        return 'Impossibile contattare il backend. Verifica la connessione.';
      }
      return `Impossibile aggiornare i servizi affini (${error.status}).`;
    }

    return 'Impossibile aggiornare i servizi affini.';
  }

  copyServiziAccessoriUrl(): void {
    if (this.serviziAccessoriUrl) {
      navigator.clipboard.writeText(this.serviziAccessoriUrl).then(() => {
        this.messageService.add({
          severity: 'info',
          summary: 'Copiato',
          detail: 'URL copiato negli appunti.',
          life: 2000,
        });
      });
    }
  }

  private escapeHtml(value: string): string {
    return (value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  ngOnInit(): void {
    // Attendi che Auth0 completi il bootstrap e l'utente sia autenticato
    combineLatest([this.auth.isLoading$, this.auth.isAuthenticated$])
      .pipe(
        filter(
          ([isLoading, isAuthenticated]) =>
            !isLoading && isAuthenticated === true,
        ),
        take(1),
      )
      .subscribe(async () => {
        // Applica i parametri di ricerca base (testo e BIM) subito
        this.route.queryParams.pipe(take(1)).subscribe((params) => {
          if (params['q']) {
            this.filterText = decodeURIComponent(params['q']);
          }
          if (params['bim'] === 'true') {
            this.bimOnly = true;
          }
          if (params['sa'] === 'true') {
            this.serviziAccessoriOnly = true;
          }
        });

        this.loadTags(); // I tag verranno applicati dopo il caricamento
        this.loadFilterRuoliProfili();
        await this.loadFirestoreConfiguration();
        // await this.testBackendHealth();
      });
  }

  ngAfterViewInit(): void {
    // Non serve più inizializzare il menu
  }

  ngOnDestroy(): void {
    // Cleanup sottoscrizioni
    if (this.referenzeSub) {
      this.referenzeSub.unsubscribe();
    }
  }

  /**
   * Carica i tag dal backend e li raggruppa per categoria
   */
  private loadTags(): void {
    this.tagService.getTags().subscribe({
      next: (tags) => {
        this.allTags = tags;
        const grouped: Record<string, TagModel[]> = {};
        for (const tag of tags) {
          if (!grouped[tag.categoriaTagCodice]) {
            grouped[tag.categoriaTagCodice] = [];
          }
          grouped[tag.categoriaTagCodice].push(tag);
        }
        this.tagsByCategory = grouped;
        Object.keys(grouped).forEach((cat) => {
          if (!this.selectedTagsByCategory[cat]) {
            this.selectedTagsByCategory[cat] = [];
          }
        });
        this.updateGroupedCategories();
        this.logger.info(
          '[HOME] Tag caricati, categorie:',
          Object.keys(grouped).length,
        );

        // Dopo aver caricato i tag, applica i parametri di ricerca dall'URL se presenti
        this.route.queryParams.pipe(take(1)).subscribe((params) => {
          if (Object.keys(params).length > 0) {
            this.logger.info('[HOME] Parametri URL rilevati:', params);
            // Converte l'oggetto params in una query string per la deserializzazione
            // NON fare encodeURIComponent perché i params sono già decodificati da Angular Router
            const queryString = Object.keys(params)
              .map((key) => `${key}=${params[key]}`)
              .join('&');
            this.logger.info('[HOME] Query string generata:', queryString);
            const searchParams =
              this.searchShareService.deserializeSearchParams(queryString);
            this.logger.info('[HOME] Parametri deserializzati:', searchParams);
            this.applySearchParams(searchParams);
          }
        });
      },
      error: (err) => {
        this.logger.error('[HOME] Errore nel caricamento dei tag:', err);
        this.segnalaBackendNonRaggiungibile();
      },
    });
  }

  /**
   * Avvisa una volta sola che i dati serviti dal backend non sono arrivati.
   *
   * All'avvio della home partono più chiamate (tag, ruoli, profili): se il
   * backend è spento fallirebbero tutte, quindi l'avviso è unico e generico.
   * Le referenze arrivano da Firestore e restano visibili, per cui senza
   * questo messaggio la pagina sembrerebbe a posto pur essendo incompleta.
   */
  private segnalaBackendNonRaggiungibile(): void {
    if (this.avvisoBackendMostrato) {
      return;
    }
    this.avvisoBackendMostrato = true;

    this.messageService.add({
      severity: 'error',
      summary: 'Dati non disponibili',
      detail:
        'Impossibile contattare il server: filtri e dati di supporto potrebbero mancare. Controlla la connessione e ricarica la pagina.',
      life: 15000,
    });
  }

  /**
   * Chiamata quando cambiano i filtri (serve solo per log/debug)
   */
  onFiltersChanged(): void {
    // PrimeNG showClear imposta null: normalizza per evitare errori su .length/.trim()
    this.selectedClienti = this.selectedClienti ?? [];
    this.selectedProjectManagers = this.selectedProjectManagers ?? [];
    this.selectedCategorieServizio = this.selectedCategorieServizio ?? [];
    this.selectedRuoliFilter = this.selectedRuoliFilter ?? [];
    this.selectedPersoneFilter = this.selectedPersoneFilter ?? [];
    this.selectedCommessa = this.selectedCommessa ?? '';
    this.selectedCertificato = this.selectedCertificato ?? '';

    // Mantieni coerenti le selezioni crociate ruolo ↔ persona
    this.prunePersoneFilterByRuoli();
    this.pruneRuoliFilterByPersone();

    Object.keys(this.selectedTagsByCategory).forEach((cat) => {
      this.selectedTagsByCategory[cat] = this.selectedTagsByCategory[cat] ?? [];
    });

    this.logger.debug('[HOME] Filtri aggiornati', {
      filterText: this.filterText,
      bimOnly: this.bimOnly,
      serviziAccessoriOnly: this.serviziAccessoriOnly,
      selectedClienti: this.selectedClienti,
      selectedProjectManagers: this.selectedProjectManagers,
      selectedCategorieServizio: this.selectedCategorieServizio,
      selectedCommessa: this.selectedCommessa,
      selectedCertificato: this.selectedCertificato,
      selectedTagsByCategory: this.selectedTagsByCategory,
    });

    // Aggiorna l'URL in base ai filtri attivi
    this.updateUrlFromFilters();
  }

  /**
   * Persone disponibili nel filtro: se ci sono tipi ruolo selezionati,
   * mostra solo chi ha almeno uno di quei ruoli nelle referenze.
   */
  get filteredPersoneFilterOptions(): Array<{ label: string; value: number }> {
    if ((this.selectedRuoliFilter?.length ?? 0) === 0) {
      return this.personeFilterOptions;
    }

    const ruoloSet = new Set(this.selectedRuoliFilter);
    const profiloIds = new Set<number>();
    for (const ref of this.referenze) {
      for (const rp of ref.ruoliProfili ?? []) {
        if (ruoloSet.has(rp.ruoloCodice) && rp.profiloId != null) {
          profiloIds.add(rp.profiloId);
        }
      }
    }

    return this.personeFilterOptions.filter((o) => profiloIds.has(o.value));
  }

  /**
   * Ruoli disponibili nel filtro: se ci sono persone selezionate,
   * mostra solo i tipi ruolo associati ad almeno una di esse nelle referenze.
   */
  get filteredRuoliFilterOptions(): Array<{
    label: string;
    value: string;
    codice: string;
  }> {
    if ((this.selectedPersoneFilter?.length ?? 0) === 0) {
      return this.ruoliFilterOptions;
    }

    const personaSet = new Set(this.selectedPersoneFilter);
    const ruoloCodici = new Set<string>();
    for (const ref of this.referenze) {
      for (const rp of ref.ruoliProfili ?? []) {
        if (personaSet.has(rp.profiloId) && rp.ruoloCodice) {
          ruoloCodici.add(rp.ruoloCodice);
        }
      }
    }

    return this.ruoliFilterOptions.filter((o) => ruoloCodici.has(o.value));
  }

  private prunePersoneFilterByRuoli(): void {
    if ((this.selectedPersoneFilter?.length ?? 0) === 0) return;
    const allowed = new Set(
      this.filteredPersoneFilterOptions.map((o) => o.value),
    );
    this.selectedPersoneFilter = this.selectedPersoneFilter.filter((id) =>
      allowed.has(id),
    );
  }

  private pruneRuoliFilterByPersone(): void {
    if ((this.selectedRuoliFilter?.length ?? 0) === 0) return;
    const allowed = new Set(
      this.filteredRuoliFilterOptions.map((o) => o.value),
    );
    this.selectedRuoliFilter = this.selectedRuoliFilter.filter((codice) =>
      allowed.has(codice),
    );
  }

  /**
   * Aggiorna l'URL in base ai filtri correnti (senza ricaricare la pagina)
   */
  private updateUrlFromFilters(): void {
    // Se non ci sono filtri attivi, pulisce l'URL
    if (!this.hasActiveFilters()) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
      return;
    }

    // Se ci sono filtri attivi, genera i query params
    const searchParams = {
      filterText: this.filterText,
      bimOnly: this.bimOnly,
      serviziAccessoriOnly: this.serviziAccessoriOnly,
      selectedClienti: this.selectedClienti,
      selectedProjectManagers: this.selectedProjectManagers,
      selectedCategorieServizio: this.selectedCategorieServizio,
      selectedCommessa: this.selectedCommessa,
      selectedCertificato: this.selectedCertificato,
      selectedTagsByCategory: this.selectedTagsByCategory,
    };

    // Genera la query string
    const queryString =
      this.searchShareService.serializeSearchParams(searchParams);

    // Costruisce i query params dall'URL generata
    const urlParams = new URLSearchParams(queryString);
    const queryParams: any = {};
    urlParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    // Naviga con i nuovi query params (senza ricaricare la pagina)
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  /**
   * Applica i parametri di ricerca dai query params
   */
  private applySearchParams(params: any): void {
    if (params.filterText) {
      this.filterText = params.filterText;
    }
    if (params.bimOnly !== undefined) {
      this.bimOnly = params.bimOnly;
    }
    if (params.serviziAccessoriOnly !== undefined) {
      this.serviziAccessoriOnly = params.serviziAccessoriOnly;
    }
    if (Array.isArray(params.selectedClienti)) {
      this.selectedClienti = params.selectedClienti;
    }
    if (Array.isArray(params.selectedProjectManagers)) {
      this.selectedProjectManagers = params.selectedProjectManagers;
    }
    if (Array.isArray(params.selectedCategorieServizio)) {
      this.selectedCategorieServizio = params.selectedCategorieServizio;
    }
    if (params.selectedCommessa) {
      this.selectedCommessa = params.selectedCommessa;
      this.logger.info('[HOME] Commessa selezionata:', this.selectedCommessa);
    }
    if (params.selectedCertificato) {
      this.selectedCertificato = params.selectedCertificato;
    }
    if (params.selectedTagsByCategory) {
      // Assicurati che le categorie esistano prima di applicare i tag
      Object.keys(params.selectedTagsByCategory).forEach((cat) => {
        if (!this.selectedTagsByCategory[cat]) {
          this.selectedTagsByCategory[cat] = [];
        }
        // Applica i tag solo se la categoria esiste nei tag caricati
        if (this.tagsByCategory[cat]) {
          // Verifica che i tag esistano nella categoria prima di applicarli
          const validTags = params.selectedTagsByCategory[cat].filter(
            (tagCode: string) => {
              return this.tagsByCategory[cat].some((t) => t.codice === tagCode);
            },
          );
          this.selectedTagsByCategory[cat] = validTags;
        }
      });
      // Aggiorna le categorie raggruppate dopo aver applicato i tag
      this.updateGroupedCategories();
    }
  }

  /**
   * Apre il dialog di condivisione
   */
  openShareMenu(event: Event): void {
    event.stopPropagation();

    const searchParams = {
      filterText: this.filterText,
      bimOnly: this.bimOnly,
      serviziAccessoriOnly: this.serviziAccessoriOnly,
      selectedClienti: this.selectedClienti,
      selectedProjectManagers: this.selectedProjectManagers,
      selectedCategorieServizio: this.selectedCategorieServizio,
      selectedCommessa: this.selectedCommessa,
      selectedCertificato: this.selectedCertificato,
      selectedTagsByCategory: this.selectedTagsByCategory,
    };

    // Verifica se ci sono filtri attivi
    if (!this.searchShareService.hasActiveParams(searchParams)) {
      this.messageService.add({
        severity: 'info',
        summary: 'Nessun filtro attivo',
        detail: 'Applica dei filtri prima di condividere la ricerca',
        life: 3000,
      });
      return;
    }

    this.showShareDialog = true;
  }

  /**
   * Chiude il dialog di condivisione
   */
  closeShareDialog(): void {
    this.showShareDialog = false;
  }

  /**
   * Apre il dialog di help per i filtri
   */
  openHelpDialog(): void {
    this.showHelpDialog = true;
  }

  /**
   * Chiude il dialog di help
   */
  closeHelpDialog(): void {
    this.showHelpDialog = false;
  }

  /**
   * Genera l'URL di condivisione completo
   * L'URL punta sempre alla home (/), indipendentemente dalla pagina corrente
   */
  private getShareUrl(): string {
    const searchParams = {
      filterText: this.filterText,
      bimOnly: this.bimOnly,
      serviziAccessoriOnly: this.serviziAccessoriOnly,
      selectedClienti: this.selectedClienti,
      selectedProjectManagers: this.selectedProjectManagers,
      selectedCategorieServizio: this.selectedCategorieServizio,
      selectedCommessa: this.selectedCommessa,
      selectedCertificato: this.selectedCertificato,
      selectedTagsByCategory: this.selectedTagsByCategory,
    };
    // Forza sempre l'URL alla home, indipendentemente dalla pagina corrente
    // Rimuove eventuali pathname dalla URL corrente per assicurarsi che punti sempre a /
    const origin = window.location.origin;
    const baseUrl = origin.endsWith('/') ? origin : origin + '/';
    const shareUrl = this.searchShareService.generateShareUrl(
      baseUrl,
      searchParams,
    );
    this.logger.debug('[HOME] URL di condivisione generato:', shareUrl);
    return shareUrl;
  }

  /**
   * Genera il testo per la condivisione
   */
  private getShareText(): string {
    const count = this.filteredReferenze.length;
    const total = this.referenze.length;
    let text = `🔍 Risultati ricerca REFHub\n\n`;

    if (this.filterText) {
      text += `Ricerca: ${this.filterText}\n`;
    }
    if (this.bimOnly) {
      text += `Filtro: Solo referenze BIM\n`;
    }
    if (this.serviziAccessoriOnly) {
      text += `Filtro: Solo referenze con servizi affini\n`;
    }
    const selectedTags = this.getSelectedTags();
    if (selectedTags.length > 0) {
      text += `Tag: ${selectedTags.map((t) => `${t.categoryName} - ${t.tagName}`).join(', ')}\n`;
    }

    text += `\n📊 ${count} di ${total} referenze trovate\n`;
    text += `🔗 Visualizza i risultati:`;

    return text;
  }

  /**
   * Copia l'URL negli appunti
   */
  async copyUrlToClipboard(): Promise<void> {
    const shareUrl = this.getShareUrl();

    try {
      await navigator.clipboard.writeText(shareUrl);
      this.messageService.add({
        severity: 'success',
        summary: 'Link copiato!',
        detail: 'Il link è stato copiato negli appunti',
        life: 3000,
      });
      this.logger.info('[HOME] Link copiato:', shareUrl);
    } catch (err) {
      this.logger.error('[HOME] Errore durante la copia:', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail:
          'Impossibile copiare il link. Prova con un altro metodo di condivisione.',
        life: 5000,
      });
    }
  }

  /**
   * Condivide via WhatsApp
   */
  shareViaWhatsApp(): void {
    const shareUrl = this.getShareUrl();
    const text = encodeURIComponent(this.getShareText() + ' ' + shareUrl);
    const whatsappUrl = `https://wa.me/?text=${text}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  }

  /**
   * Condivide via Email
   */
  shareViaEmail(): void {
    const shareUrl = this.getShareUrl();
    const subject = encodeURIComponent('Risultati ricerca REFHub');
    const body = encodeURIComponent(this.getShareText() + '\n\n' + shareUrl);
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = mailtoUrl;
  }

  /**
   * Condivide via LinkedIn
   */
  shareViaLinkedIn(): void {
    const shareUrl = encodeURIComponent(this.getShareUrl());
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`;
    window.open(
      linkedInUrl,
      '_blank',
      'noopener,noreferrer,width=600,height=400',
    );
  }

  /**
   * Condivide via Twitter/X
   */
  shareViaTwitter(): void {
    const shareUrl = encodeURIComponent(this.getShareUrl());
    const text = encodeURIComponent(
      `Risultati ricerca REFHub - ${this.filteredReferenze.length} referenze trovate`,
    );
    const twitterUrl = `https://twitter.com/intent/tweet?url=${shareUrl}&text=${text}`;
    window.open(
      twitterUrl,
      '_blank',
      'noopener,noreferrer,width=600,height=400',
    );
  }

  /**
   * Condivide via Facebook
   */
  shareViaFacebook(): void {
    const shareUrl = encodeURIComponent(this.getShareUrl());
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`;
    window.open(
      facebookUrl,
      '_blank',
      'noopener,noreferrer,width=600,height=400',
    );
  }

  /**
   * Ottiene tutti i tag selezionati con le loro informazioni
   */
  getSelectedTags(): Array<{
    categoryCode: string;
    categoryName: string;
    tagCode: string;
    tagName: string;
  }> {
    const selected: Array<{
      categoryCode: string;
      categoryName: string;
      tagCode: string;
      tagName: string;
    }> = [];

    Object.keys(this.selectedTagsByCategory).forEach((catCode) => {
      const tagCodes = this.selectedTagsByCategory[catCode] || [];
      const categoryTags = this.tagsByCategory[catCode] || [];

      tagCodes.forEach((tagCode) => {
        const tag = categoryTags.find((t) => t.codice === tagCode);
        const categoryName = categoryTags[0]?.categoriaTagNome || catCode;
        selected.push({
          categoryCode: catCode,
          categoryName,
          tagCode,
          tagName: tag?.codice || tagCode,
        });
      });
    });

    return selected;
  }

  /**
   * Rimuove un tag specifico dai filtri
   */
  removeTag(categoryCode: string, tagCode: string): void {
    if (this.selectedTagsByCategory[categoryCode]) {
      this.selectedTagsByCategory[categoryCode] = this.selectedTagsByCategory[
        categoryCode
      ].filter((code) => code !== tagCode);
      this.onFiltersChanged();
    }
  }

  /**
   * Cancella tutti i filtri applicati
   */
  clearAllFilters(): void {
    this.filterText = '';
    this.bimOnly = false;
    this.serviziAccessoriOnly = false;
    this.selectedClienti = [];
    this.selectedProjectManagers = [];
    this.selectedCategorieServizio = [];
    this.selectedCommessa = '';
    this.selectedCertificato = '';
    this.selectedRuoliFilter = [];
    this.selectedPersoneFilter = [];
    Object.keys(this.selectedTagsByCategory).forEach((cat) => {
      this.selectedTagsByCategory[cat] = [];
    });
    this.onFiltersChanged();
  }

  /**
   * Verifica se ci sono filtri attivi
   */
  hasActiveFilters(): boolean {
    const hasTextFilter = this.filterText.trim().length > 0;
    const hasBimFilter = this.bimOnly;
    const hasServiziAccessoriFilter = this.serviziAccessoriOnly;
    const hasClienti = (this.selectedClienti?.length ?? 0) > 0;
    const hasPm = (this.selectedProjectManagers?.length ?? 0) > 0;
    const hasCatServizio = (this.selectedCategorieServizio?.length ?? 0) > 0;
    const hasCommessa = (this.selectedCommessa ?? '').trim().length > 0;
    const hasCertificato = (this.selectedCertificato ?? '').trim().length > 0;
    const hasRuoli = (this.selectedRuoliFilter?.length ?? 0) > 0;
    const hasPersone = (this.selectedPersoneFilter?.length ?? 0) > 0;
    const hasTagFilters = Object.keys(this.selectedTagsByCategory).some(
      (cat) => (this.selectedTagsByCategory[cat] || []).length > 0,
    );
    return (
      hasTextFilter ||
      hasBimFilter ||
      hasServiziAccessoriFilter ||
      hasClienti ||
      hasPm ||
      hasCatServizio ||
      hasCommessa ||
      hasCertificato ||
      hasRuoli ||
      hasPersone ||
      hasTagFilters
    );
  }

  /**
   * Label chip filtri strutturati
   */
  getClientiChipLabel(): string {
    if (this.selectedClienti.length === 1) {
      return `Cliente: ${this.selectedClienti[0]}`;
    }
    return `Clienti: ${this.selectedClienti.length}`;
  }

  getProjectManagersChipLabel(): string {
    if (this.selectedProjectManagers.length === 1) {
      return `PM: ${this.selectedProjectManagers[0]}`;
    }
    return `PM: ${this.selectedProjectManagers.length}`;
  }

  getCategorieServizioChipLabel(): string {
    if (this.selectedCategorieServizio.length === 1) {
      return `Categoria: ${this.selectedCategorieServizio[0]}`;
    }
    return `Categorie: ${this.selectedCategorieServizio.length}`;
  }

  getClientiTooltip(): string {
    return this.selectedClienti.join('\n');
  }

  getProjectManagersTooltip(): string {
    return this.selectedProjectManagers.join('\n');
  }

  getCategorieServizioTooltip(): string {
    return this.selectedCategorieServizio.join('\n');
  }

  clearClientiFilter(): void {
    this.selectedClienti = [];
    this.onFiltersChanged();
  }

  clearProjectManagersFilter(): void {
    this.selectedProjectManagers = [];
    this.onFiltersChanged();
  }

  clearCategorieServizioFilter(): void {
    this.selectedCategorieServizio = [];
    this.onFiltersChanged();
  }

  clearCommessaFilter(): void {
    this.selectedCommessa = '';
    this.onFiltersChanged();
  }

  clearCertificatoFilter(): void {
    this.selectedCertificato = '';
    this.onFiltersChanged();
  }

  getCommessaChipLabel(): string {
    return `Commessa: ${this.selectedCommessa}`;
  }

  getCertificatoChipLabel(): string {
    return `Certificato: ${this.selectedCertificato}`;
  }

  clearRuoliFilter(): void {
    this.selectedRuoliFilter = [];
    this.onFiltersChanged();
  }

  onRuoliFilterChanged(): void {
    this.onFiltersChanged();
    this.ruoliFilter?.hide();
  }

  clearPersoneFilter(): void {
    this.selectedPersoneFilter = [];
    this.onFiltersChanged();
  }

  onPersoneFilterChanged(): void {
    this.onFiltersChanged();
    this.personeFilter?.resetFilter();
    this.personeFilter?.hide();
  }

  getRuoliFilterChipLabel(): string {
    if (this.selectedRuoliFilter.length === 1) {
      const opt = this.ruoliFilterOptions.find(
        (o) => o.value === this.selectedRuoliFilter[0],
      );
      return `Ruolo: ${opt?.label ?? this.selectedRuoliFilter[0]}`;
    }
    return `Ruoli: ${this.selectedRuoliFilter.length}`;
  }

  getPersoneFilterChipLabel(): string {
    if (this.selectedPersoneFilter.length === 1) {
      const opt = this.personeFilterOptions.find(
        (o) => o.value === this.selectedPersoneFilter[0],
      );
      return `Persona: ${opt?.label ?? this.selectedPersoneFilter[0]}`;
    }
    return `Persone: ${this.selectedPersoneFilter.length}`;
  }

  getRuoliFilterTooltip(): string {
    return this.selectedRuoliFilter
      .map((v) => {
        const opt = this.ruoliFilterOptions.find((o) => o.value === v);
        return opt ? `${opt.codice} — ${opt.label}` : v;
      })
      .join('\n');
  }

  getPersoneFilterTooltip(): string {
    return this.selectedPersoneFilter
      .map((id) => {
        const opt = this.personeFilterOptions.find((o) => o.value === id);
        return opt?.label ?? String(id);
      })
      .join('\n');
  }

  /**
   * Options per dropdown dei tag di una categoria
   */
  getTagOptionsForCategory(
    catCode: string,
  ): Array<{ label: string; value: string }> {
    const tags = this.tagsByCategory[catCode] || [];
    return tags
      .slice()
      .sort((a, b) => {
        const la = a.codice.replace(/_/g, ' ');
        const lb = b.codice.replace(/_/g, ' ');
        return la.localeCompare(lb, 'it', { sensitivity: 'base' });
      })
      .map((t) => ({
        label: t.codice.replace(/_/g, ' '),
        value: t.codice,
      }));
  }

  /**
   * Apre il dialog di creazione tag restando in Home
   */
  onCreateNewTagFromDropdown(categoryCode: string): void {
    if (!this.canModify()) {
      return;
    }
    this.createTagInitialCategory = categoryCode;
    this.showCreateTagDialog = true;
  }

  onTagCreatedInHome(): void {
    // ricarica tags per aggiornare i dropdown e le categorie raggruppate
    this.loadTags();
  }

  /**
   * Carica ruoli e profili dai servizi per popolare i filtri,
   * indipendentemente dai dati presenti nelle referenze.
   */
  private loadFilterRuoliProfili(): void {
    Promise.all([
      firstValueFrom(this.ruoliReferenzaService.getRuoli()),
      firstValueFrom(this.profiliService.getProfili()),
    ])
      .then(([ruoli, profili]) => {
        this.ruoliFilterOptions = (ruoli ?? [])
          .sort((a, b) => a.codice.localeCompare(b.codice, 'it'))
          .map((r) => ({
            label: r.descrizione,
            value: r.codice,
            codice: r.codice,
          }));

        this.personeFilterOptions = (profili ?? [])
          .map((p) => ({
            label: this.formatPersonaFilterLabel(p.cognome, p.nome),
            value: p.id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'it'));
      })
      .catch((err) => {
        this.logger.error(
          '[HOME] Errore caricamento ruoli/profili per i filtri:',
          err,
        );
        this.segnalaBackendNonRaggiungibile();
      });
  }

  /** Cognome MAIUSCOLO + Nome con iniziale maiuscola. */
  private formatPersonaFilterLabel(cognome?: string, nome?: string): string {
    const cognomeFmt = (cognome ?? '').trim().toLocaleUpperCase('it');
    const nomeRaw = (nome ?? '').trim().toLocaleLowerCase('it');
    const nomeFmt = nomeRaw
      ? nomeRaw.charAt(0).toLocaleUpperCase('it') + nomeRaw.slice(1)
      : '';
    return `${cognomeFmt} ${nomeFmt}`.trim();
  }

  /**
   * Costruisce le options per i filtri strutturati (cliente/PM/categorie servizio)
   */
  private rebuildStructuredFilterOptions(): void {
    const unique = (vals: Array<string | null | undefined>): string[] => {
      const set = new Set<string>();
      vals.forEach((v) => {
        const s = (v || '').trim();
        if (s) set.add(s);
      });
      return Array.from(set).sort((a, b) => a.localeCompare(b, 'it'));
    };

    const clienti = unique(this.referenze.map((r) => r.cliente));
    const pms = unique(this.referenze.map((r) => r.projectManager));
    const catServ = unique(
      this.referenze.flatMap((r) => r.categorieServizio || []),
    );
    const commesse = unique(
      this.referenze.map((r) =>
        r.codiceCommerciale != null ? String(r.codiceCommerciale) : null,
      ),
    );
    const certificati = unique(
      this.referenze.flatMap((r) => this.getCertificati(r)),
    );

    this.clienteOptions = clienti.map((v) => ({ label: v, value: v }));
    this.projectManagerOptions = pms.map((v) => ({ label: v, value: v }));
    this.categorieServizioOptions = catServ.map((v) => ({
      label: v,
      value: v,
    }));
    this.commessaOptions = commesse.map((v) => ({ label: v, value: v }));
    this.certificatoOptions = certificati.map((v) => ({ label: v, value: v }));

    // Pulisci selezioni non più disponibili (basate su dati live delle referenze)
    const allowedRuoli = new Set(this.ruoliFilterOptions.map((o) => o.value));
    this.selectedRuoliFilter = this.selectedRuoliFilter.filter((v) =>
      allowedRuoli.has(v),
    );
    this.prunePersoneFilterByRuoli();
    this.pruneRuoliFilterByPersone();

    // Pulisci selezioni non più disponibili (dati cambiati live)
    const allowedClienti = new Set(clienti);
    const allowedPms = new Set(pms);
    const allowedCatServ = new Set(catServ);
    const allowedCommesse = new Set(commesse);
    const allowedCertificati = new Set(certificati);
    this.selectedClienti = this.selectedClienti.filter((v) =>
      allowedClienti.has(v),
    );
    this.selectedProjectManagers = this.selectedProjectManagers.filter((v) =>
      allowedPms.has(v),
    );
    this.selectedCategorieServizio = this.selectedCategorieServizio.filter(
      (v) => allowedCatServ.has(v),
    );
    if (this.selectedCommessa && !allowedCommesse.has(this.selectedCommessa)) {
      this.selectedCommessa = '';
    }
    if (
      this.selectedCertificato &&
      !allowedCertificati.has(this.selectedCertificato)
    ) {
      this.selectedCertificato = '';
    }
  }

  /**
   * Aggiorna i gruppi di categorie quando cambiano i tag
   */
  private updateGroupedCategories(): void {
    // Nuovo modello: un modulo per ciascuna delle 5 categorie principali
    this.groupedCategories = this.tagCategoryOrder
      .map((code) => {
        const catData = this.tagsByCategory[code];
        if (!catData || catData.length === 0) {
          return null;
        }

        // TITOLO: usa il codice categoria, ma senza underscore per TIPO_STRUTTURE
        const label = code === 'TIPO_STRUTTURE' ? 'TIPO STRUTTURE' : code;

        return {
          groupName: code,
          groupLabel: label, // mostra solo il codice categoria nel titolo modulo
          groupIcon: this.tagCategoryIcons[code] || 'category',
          categories: [{ key: code, value: catData }],
        };
      })
      .filter(
        (
          group,
        ): group is {
          groupName: string;
          groupLabel: string;
          groupIcon: string;
          categories: Array<{ key: string; value: TagModel[] }>;
        } => group !== null,
      );
  }

  /**
   * Carica la configurazione di Firestore e testa la connettività
   */
  private async loadFirestoreConfiguration(): Promise<void> {
    try {
      // Ottieni informazioni sulla configurazione
      const configInfo =
        this.firestoreConnectionService.getFirebaseConfigInfo();

      if (!configInfo.isValid) {
        this.logger.error('[HOME] ❌ Configurazione Firebase non valida');
        return;
      }

      // Testa la connettività Firestore
      const firestoreResult =
        await this.firestoreConnectionService.testConnection();

      if (firestoreResult.connected) {
        this.logger.info(
          '[HOME] ✅ Connessione Firestore stabilita con successo @ ' +
            environment.firebase.collectionReferenze,
          {
            documentsCount: firestoreResult.documentsCount,
          },
        );

        // Avvia l'ascolto in tempo reale delle referenze solo se la connessione è OK
        this.startReferenzeListener();
      } else {
        this.logger.warn('[HOME] ⚠️ Problemi con la connessione Firestore', {
          error: firestoreResult.error,
          responseTime: firestoreResult.responseTime,
        });
      }
    } catch (error) {
      this.logger.error(
        '[HOME] ❌ Errore durante il test della connessione Firestore:',
        error,
      );
    }
  }

  /**
   * Avvia l'ascolto continuo sulla collection di referenze
   */
  private startReferenzeListener(): void {
    // Evita doppie sottoscrizioni se già attivo
    if (this.referenzeSub) {
      this.referenzeSub.unsubscribe();
    }

    //this.logger.info('[HOME] Inizio ascolto referenze Firestore...');

    this.referenzeSub = this.referenzeService.listenReferenze().subscribe({
      next: (refs) => {
        this.referenze = refs;
        this.rebuildStructuredFilterOptions();
        this.logger.info(
          '[HOME] Referenze aggiornate, numero elementi:',
          refs.length,
        );
      },
      error: (error) => {
        this.logger.error("[HOME] Errore durante l'ascolto referenze:", error);
      },
    });
  }

  /**
   * Testa la connettività con le API backend tramite endpoint /health
   */
  private async testBackendHealth(): Promise<void> {
    try {
      const result = await this.apiService.testHealth();

      if (result.connected) {
        this.logger.info(
          '[HOME] ✅ Connessione backend stabilita con successo alle API @ ' +
            environment.backend.baseUrl,
        );
      } else {
        this.logger.warn('[HOME] ⚠️ Problemi con la connessione backend', {
          status: result.status,
          error: result.error,
          responseTime: result.responseTime,
        });
      }
    } catch (error) {
      this.logger.error(
        '[HOME] ❌ Errore durante il test della connessione backend:',
        error,
      );
    }
  }

  /**
   * Apre il wizard per creare una nuova referenza
   */
  openNuovaReferenzaWizard(): void {
    this.showNuovaReferenzaWizard = true;
  }

  /**
   * Callback chiamato quando una referenza è stata creata
   */
  onReferenzaCreated(): void {
    this.logger.info(
      '[HOME] Nuova referenza creata, lista referenze verrà aggiornata automaticamente',
    );
    // Il listener di Firestore aggiornerà automaticamente la lista
  }

  /**
   * Esporta i risultati filtrati in formato Excel
   */
  exportToExcel(): void {
    this.excelExportService.exportReferenze(this.filteredReferenze);
  }

  /**
   * Ottiene il colore del badge in base alla categoria del tag
   * Formato tag: "CATEGORIA#CODICE"
   */
  getTagColor(tag: string): string {
    const [category] = tag.split('#');
    if (!category) return '#6b7280'; // grigio di default

    // Mappa colori per categoria (allineata alle 5 categorie principali)
    const categoryColors: Record<string, string> = {
      FASI: '#0ea5e9', // azzurro
      TEMATICHE: '#22c55e', // verde
      TIPOLOGIA: '#8b5cf6', // viola
      PRESTAZIONI: '#f97316', // arancione
      TIPO_STRUTTURE: '#ec4899', // rosa
    };

    return categoryColors[category] || '#6b7280'; // grigio di default
  }

  /**
   * Ottiene il colore del testo (bianco o nero) in base alla luminosità del colore di sfondo
   */
  getTagTextColor(tag: string): string {
    const bgColor = this.getTagColor(tag);
    // Converti hex a RGB
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calcola la luminosità relativa
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Se la luminosità è alta, usa testo nero, altrimenti bianco
    return luminance > 0.5 ? '#1f2937' : '#ffffff';
  }

  /**
   * Estrae il nome visualizzato del tag (rimuove il prefisso categoria)
   * Formato tag: "CATEGORIA#CODICE" -> restituisce "CODICE"
   * Sostituisce gli underscore con spazi per la visualizzazione
   */
  getTagDisplayName(tag: string): string {
    const parts = tag.split('#');
    if (parts.length > 1) {
      return parts[1].replace(/_/g, ' ');
    }
    return tag.replace(/_/g, ' ');
  }

  /**
   * Ottiene i tag di una referenza filtrati per gruppo di categorie
   */
  getTagsByGroup(ref: ReferenzaFirestore, groupName: string): string[] {
    if (!ref.tag || ref.tag.length === 0) {
      return [];
    }

    const group = this.groupedCategories.find((g) => g.groupName === groupName);
    if (!group || !group.categories || group.categories.length === 0) {
      return [];
    }

    // Estrai i codici delle categorie dal gruppo
    const categoryCodes = group.categories.map((cat) => cat.key);

    return ref.tag.filter((tag) => {
      const [category] = tag.split('#');
      return categoryCodes.includes(category);
    });
  }

  /**
   * Apre il dialog per visualizzare i tag di una referenza
   */
  openTagsDialog(referenza: ReferenzaFirestore): void {
    this.selectedReferenzaForTags = referenza;
    this.showTagsDialog = true;
  }

  /**
   * Chiude il dialog dei tag
   */
  closeTagsDialog(): void {
    this.showTagsDialog = false;
    this.selectedReferenzaForTags = null;
    this.selectedCategoryForNewTag = null;
    this.selectedTagForNewTag = null;
  }

  openCategoriServizioDialog(referenza: ReferenzaFirestore): void {
    this.selectedReferenzaForCategoriServizio = referenza;
    this.showCategoriServizioDialog = true;
  }

  closeCategoriServizioDialog(): void {
    this.showCategoriServizioDialog = false;
    this.selectedReferenzaForCategoriServizio = null;
    this.newCategoriaServizio = null;
  }

  getCategoriServizioOptions(): { label: string; value: string }[] {
    const current =
      this.selectedReferenzaForCategoriServizio?.categorieServizio ?? [];
    return this.tutteLeCategoriServizio
      .filter((cat) => !current.includes(cat))
      .map((cat) => ({ label: cat, value: cat }));
  }

  confirmRemoveCategoriaServizio(cat: string): void {
    if (!this.selectedReferenzaForCategoriServizio) return;
    this.confirmationService.confirm({
      message: `Sei sicuro di voler rimuovere la categoria <strong>${cat}</strong> dalla referenza?`,
      header: 'Conferma Rimozione',
      icon: 'none',
      acceptLabel: 'Rimuovi',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.removeCategoriaServizio(cat),
    });
  }

  async removeCategoriaServizio(cat: string): Promise<void> {
    const ref = this.selectedReferenzaForCategoriServizio;
    if (!ref) return;
    this.savingCategoriaServizio = true;
    try {
      const updated = (ref.categorieServizio ?? []).filter((c) => c !== cat);
      await firstValueFrom(
        this.apiService.updateReferenza(
          ref.id,
          this.buildCategoriServizioDto(ref, updated),
        ),
      );
      ref.categorieServizio = updated;
      this.messageService.add({
        severity: 'success',
        summary: 'Rimossa',
        detail: `Categoria ${cat} rimossa`,
        life: 3000,
      });
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile rimuovere la categoria',
        life: 5000,
      });
    } finally {
      this.savingCategoriaServizio = false;
    }
  }

  async addCategoriaServizio(): Promise<void> {
    const ref = this.selectedReferenzaForCategoriServizio;
    if (!ref || !this.newCategoriaServizio) return;
    this.savingCategoriaServizio = true;
    try {
      const updated = [
        ...(ref.categorieServizio ?? []),
        this.newCategoriaServizio,
      ];
      await firstValueFrom(
        this.apiService.updateReferenza(
          ref.id,
          this.buildCategoriServizioDto(ref, updated),
        ),
      );
      ref.categorieServizio = updated;
      this.newCategoriaServizio = null;
      this.messageService.add({
        severity: 'success',
        summary: 'Aggiunta',
        detail: `Categoria aggiunta`,
        life: 3000,
      });
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile aggiungere la categoria',
        life: 5000,
      });
    } finally {
      this.savingCategoriaServizio = false;
    }
  }

  private buildCategoriServizioDto(
    ref: ReferenzaFirestore,
    categorie: string[],
  ): ReferenzaModel {
    const tagRiferimenti = (ref.tag ?? []).map((t) => {
      const [codiceCategoria, codiceTag] = t.split('#');
      return { codiceCategoria, codiceTag };
    });
    return {
      id: ref.id,
      codiceCommessa:
        ref.codiceCommerciale != null ? String(ref.codiceCommerciale) : null,
      codiceCertificato:
        ref.codiceCertificato != null
          ? (ref.codiceCertificato as number)
          : null,
      projectManager: ref.projectManager || null,
      cliente: ref.cliente || null,
      titoloBreve: ref.titoloBreve,
      titoloEsteso: ref.titoloEsteso || null,
      tempistiche: ref.tempistiche || null,
      dataFine: ref.dataFine || null,
      rtp: ref.rtp || false,
      capogruppo: ref.capogruppo || false,
      quotaCooprogetti: ref.quotaCooprogetti ?? null,
      inAq: ref.inAq || false,
      bim: ref.bim || false,
      valoreDimensione: ref.valoreDimensione || null,
      importoLavori: ref.importoLavori ?? null,
      importoServizio: ref.importoServizio ?? null,
      categorieServizio: categorie.length > 0 ? categorie : null,
      tag: tagRiferimenti.length > 0 ? tagRiferimenti : null,
      dataCreazione: ref.dataCreazione || null,
      dataModifica: new Date().toISOString(),
    } as ReferenzaModel;
  }

  /**
   * Apre il dialog per gestire i ruoli assegnati a una referenza.
   * Carica in parallelo ruoli disponibili e profili (solo la prima volta).
   */
  async openRuoliDialog(referenza: ReferenzaFirestore): Promise<void> {
    this.selectedReferenzaForRuoli = referenza;
    this.showRuoliDialog = true;
    this.newRuoloId = null;
    this.newProfiloId = null;
    this.newFirmatario = false;

    this.logger.debug(
      `[HOME] Ruoli letti per "${referenza.titoloBreve}": ${referenza.ruoliProfili?.length ?? 0}`,
      referenza.ruoliProfili ?? [],
    );

    if (
      this.ruoliDisponibili.length === 0 ||
      this.profiliDisponibili.length === 0
    ) {
      this.loadingRuoliDialog = true;
      try {
        const [ruoli, profili] = await Promise.all([
          firstValueFrom(this.ruoliReferenzaService.getRuoli()),
          firstValueFrom(this.profiliService.getProfili()),
        ]);
        this.ruoliDisponibili = ruoli ?? [];
        this.profiliDisponibili = (profili ?? []).map((p) => ({
          ...p,
          displayName: `${p.nome} ${p.cognome}`.trim(),
        }));
      } catch (err) {
        this.logger.error(
          '[HOME] Errore caricamento ruoli/profili per il dialog:',
          err,
        );
      } finally {
        this.loadingRuoliDialog = false;
      }
    }
  }

  /**
   * Chiude il dialog dei ruoli e resetta lo stato del form
   */
  closeRuoliDialog(): void {
    this.showRuoliDialog = false;
    this.selectedReferenzaForRuoli = null;
    this.newRuoloId = null;
    this.newProfiloId = null;
    this.newFirmatario = false;
    this.closeCreateProfiloDialog();
  }

  openCreateProfiloDialog(): void {
    this.newProfiloNome = '';
    this.newProfiloCognome = '';
    this.savingNewProfilo = false;
    this.showCreateProfiloDialog = true;
  }

  closeCreateProfiloDialog(): void {
    this.showCreateProfiloDialog = false;
    this.newProfiloNome = '';
    this.newProfiloCognome = '';
    this.savingNewProfilo = false;
  }

  isNewProfiloFormValid(): boolean {
    return (
      this.newProfiloNome.trim().length > 0 &&
      this.newProfiloCognome.trim().length > 0
    );
  }

  private isProfiloDuplicate(nome: string, cognome: string): boolean {
    const normalize = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();
    const nomeNorm = normalize(nome);
    const cognomeNorm = normalize(cognome);
    return this.profiliDisponibili.some(
      (p) => normalize(p.nome) === nomeNorm && normalize(p.cognome) === cognomeNorm,
    );
  }

  async createAndSelectProfilo(): Promise<void> {
    if (!this.isNewProfiloFormValid() || this.savingNewProfilo) return;

    const nome = this.newProfiloNome.trim();
    const cognome = this.newProfiloCognome.trim();
    if (this.isProfiloDuplicate(nome, cognome)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Profilo già presente',
        detail: `Esiste già un profilo per ${nome} ${cognome}.`,
        life: 4000,
      });
      return;
    }

    this.savingNewProfilo = true;
    try {
      const created = await firstValueFrom(
        this.profiliService.createProfilo({ nome, cognome }),
      );
      const nuovoProfilo: ProfiloModel = {
        ...created,
        displayName: `${created.nome} ${created.cognome}`.trim(),
      };

      this.profiliDisponibili = [...this.profiliDisponibili, nuovoProfilo];
      this.newProfiloId = nuovoProfilo.id;
      this.closeCreateProfiloDialog();

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: `${nuovoProfilo.displayName} creato e selezionato`,
        life: 3000,
      });
    } catch (err) {
      this.logger.error('[HOME] Errore nella creazione del profilo:', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile creare il profilo. Riprova.',
        life: 5000,
      });
    } finally {
      this.savingNewProfilo = false;
    }
  }

  /**
   * Lista flat di ruoliProfili per la tabella, ordinata per codice ruolo
   */
  getRuoliFlat(): RuoloProfilo[] {
    return [...(this.selectedReferenzaForRuoli?.ruoliProfili ?? [])].sort(
      (a, b) => a.ruoloCodice.localeCompare(b.ruoloCodice),
    );
  }

  /**
   * Evidenzia le righe della maschera ruoli che corrispondono ai filtri attivi.
   */
  isRuoloProfiloHighlighted(rp: RuoloProfilo): boolean {
    const hasRuoli = (this.selectedRuoliFilter?.length ?? 0) > 0;
    const hasPersone = (this.selectedPersoneFilter?.length ?? 0) > 0;
    if (!hasRuoli && !hasPersone) return false;

    const ruoloOk =
      !hasRuoli || this.selectedRuoliFilter.includes(rp.ruoloCodice);
    const personaOk =
      !hasPersone || this.selectedPersoneFilter.includes(rp.profiloId);
    return ruoloOk && personaOk;
  }

  /**
   * Options per il dropdown dei ruoli disponibili
   */
  getRuoliOptions(): Array<{ label: string; value: number }> {
    return (
      this.ruoliDisponibili
        .slice()
        //.sort((a, b) => (a.ordineVisualizzazione ?? 999) - (b.ordineVisualizzazione ?? 999))
        .map((r) => ({ label: r.descrizione, value: r.id }))
    );
  }

  /**
   * Options per il dropdown dei profili.
   * Esclude i profili già assegnati allo stesso ruolo selezionato.
   */
  getProfiliOptions(): Array<{ label: string; value: number }> {
    const ruoloSelezionato = this.ruoliDisponibili.find(
      (r) => r.id === this.newRuoloId,
    );
    const assegnatiAlRuolo = new Set(
      (this.selectedReferenzaForRuoli?.ruoliProfili ?? [])
        .filter(
          (rp) =>
            ruoloSelezionato && rp.ruoloCodice === ruoloSelezionato.codice,
        )
        .map((rp) => rp.profiloId),
    );
    return this.profiliDisponibili
      .filter((p) => !assegnatiAlRuolo.has(p.id))
      .sort((a, b) => {
        const byCognome = a.cognome.localeCompare(b.cognome, 'it', {
          sensitivity: 'base',
        });
        if (byCognome !== 0) return byCognome;
        return a.nome.localeCompare(b.nome, 'it', { sensitivity: 'base' });
      })
      .map((p) => ({
        label: p.displayName ?? `${p.nome} ${p.cognome}`.trim(),
        value: p.id,
      }));
  }

  /**
   * True se il form di aggiunta ha ruolo e profilo selezionati
   */
  isNewRuoloFormValid(): boolean {
    return this.newRuoloId !== null && this.newProfiloId !== null;
  }

  /**
   * Aggiunge l'assegnazione ruolo/profilo alla referenza
   */
  async addRuoloProfilo(): Promise<void> {
    if (!this.selectedReferenzaForRuoli || !this.isNewRuoloFormValid()) return;

    const ruolo = this.ruoliDisponibili.find((r) => r.id === this.newRuoloId)!;

    this.savingRuolo = true;
    try {
      const profilo = this.profiliDisponibili.find(
        (p) => p.id === this.newProfiloId,
      )!;
      await firstValueFrom(
        this.apiService.assignProfiloToRuolo(
          this.selectedReferenzaForRuoli.id,
          ruolo.id,
          profilo.id,
          { firmatario: this.newFirmatario },
        ),
      );
      const nuovo: RuoloProfilo = {
        profiloId: profilo.id,
        nomeCompleto:
          profilo.displayName ?? `${profilo.nome} ${profilo.cognome}`.trim(),
        ruoloCodice: ruolo.codice,
        ruoloDescrizione: ruolo.descrizione,
        firmatario: this.newFirmatario,
      };
      this.selectedReferenzaForRuoli.ruoliProfili = [
        ...(this.selectedReferenzaForRuoli.ruoliProfili ?? []),
        nuovo,
      ];

      this.newRuoloId = null;
      this.newProfiloId = null;
      this.newFirmatario = false;

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: `${nuovo.nomeCompleto} assegnato come ${ruolo.descrizione}`,
        life: 3000,
      });
      this.logger.info('[HOME] Ruolo/profilo aggiunto con successo');
    } catch (err) {
      this.logger.error("[HOME] Errore nell'aggiunta del ruolo/profilo:", err);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: "Impossibile aggiungere l'assegnazione. Riprova.",
        life: 5000,
      });
    } finally {
      this.savingRuolo = false;
    }
  }

  /**
   * Chiede conferma prima di rimuovere un'assegnazione ruolo/profilo
   */
  confirmRemoveRuoloProfilo(rp: RuoloProfilo): void {
    this.confirmationService.confirm({
      message: `
        <div class="delete-confirm-message">
          <p class="confirm-text">Rimuovere questa assegnazione?</p>
          <div class="tag-details">
            <div class="detail-row">
              <span class="detail-label">Ruolo:</span>
              <span class="detail-value">${rp.ruoloDescrizione}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Persona:</span>
              <span class="detail-value">${rp.nomeCompleto}</span>
            </div>
          </div>
        </div>
      `,
      header: 'Conferma Rimozione',
      icon: 'none',
      acceptLabel: 'Rimuovi',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => this.removeRuoloProfilo(rp),
    });
  }

  /**
   * Rimuove l'assegnazione ruolo/profilo dalla referenza
   */
  async removeRuoloProfilo(rp: RuoloProfilo): Promise<void> {
    if (!this.selectedReferenzaForRuoli) return;

    const ruolo = this.ruoliDisponibili.find(
      (r) => r.codice === rp.ruoloCodice,
    );
    if (!ruolo) {
      this.logger.error('[HOME] Ruolo non trovato per codice:', rp.ruoloCodice);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Ruolo non trovato nei dati disponibili. Ricarica la pagina.',
        life: 5000,
      });
      return;
    }

    try {
      await firstValueFrom(
        this.apiService.removeProfiloFromRuolo(
          this.selectedReferenzaForRuoli.id,
          ruolo.id,
          rp.profiloId,
        ),
      );

      // Aggiornamento ottimistico locale
      this.selectedReferenzaForRuoli.ruoliProfili = (
        this.selectedReferenzaForRuoli.ruoliProfili ?? []
      ).filter(
        (r) =>
          !(r.ruoloCodice === rp.ruoloCodice && r.profiloId === rp.profiloId),
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Assegnazione rimossa con successo',
        life: 3000,
      });
      this.logger.info('[HOME] Ruolo/profilo rimosso con successo');
    } catch (err) {
      this.logger.error(
        '[HOME] Errore nella rimozione del ruolo/profilo:',
        err,
      );
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: "Impossibile rimuovere l'assegnazione. Riprova.",
        life: 5000,
      });
    }
  }

  // Proprietà per aggiungere tag
  selectedCategoryForNewTag: string | null = null;
  selectedTagForNewTag: TagModel | null = null;

  /**
   * Carica i tag disponibili per una categoria quando viene selezionata
   */
  onCategorySelectedForNewTag(event: any): void {
    const categoryCode = event.value;
    if (!categoryCode) {
      this.selectedCategoryForNewTag = null;
      this.selectedTagForNewTag = null;
      return;
    }

    this.selectedCategoryForNewTag = categoryCode;
    this.selectedTagForNewTag = null; // Reset del tag selezionato quando cambia categoria
  }

  /**
   * Gestisce la selezione di un tag dal dropdown (non aggiunge automaticamente)
   */
  onTagSelectedFromDropdown(event: any): void {
    // Non facciamo nulla qui, l'aggiunta avviene solo quando si clicca sul pulsante "Aggiungi"
  }

  /**
   * Aggiunge il tag selezionato alla referenza (chiamato dal pulsante "Aggiungi")
   */
  onAddTagClick(): void {
    if (!this.selectedTagForNewTag) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Seleziona un tag prima di aggiungerlo',
        life: 3000,
      });
      return;
    }

    // Il dropdown restituisce l'oggetto TagModel direttamente come value
    const selectedTag: TagModel = this.selectedTagForNewTag;

    this.logger.debug('[HOME] Tag selezionato per aggiunta:', selectedTag);

    // Verifica che il tag abbia tutte le proprietà necessarie
    if (
      !selectedTag ||
      !selectedTag.categoriaTagCodice ||
      !selectedTag.codice
    ) {
      this.logger.error('[HOME] Tag non valido:', selectedTag);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Tag non valido. Seleziona un tag valido.',
        life: 3000,
      });
      return;
    }

    // Verifica se il tag è già presente prima di aggiungerlo
    if (this.isTagAlreadyPresent(selectedTag)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Questo tag è già presente nella referenza',
        life: 3000,
      });
      this.selectedTagForNewTag = null;
      return;
    }

    this.addTagToReferenza(selectedTag);
    // Reset del dropdown dopo l'aggiunta
    this.selectedTagForNewTag = null;
  }

  /**
   * Gestisce l'apertura del dropdown della categoria per forzare l'apertura verso l'alto
   */
  onCategoryDropdownShow(event: any): void {
    // Aspetta che il DOM si aggiorni e che PrimeNG posizioni il panel
    setTimeout(() => {
      const dropdown = document.getElementById('category-select');
      if (!dropdown) return;

      const dropdownRect = dropdown.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - dropdownRect.bottom;
      const spaceAbove = dropdownRect.top;

      // Trova il panel del dropdown (potrebbe essere l'ultimo creato)
      const panels = Array.from(
        document.querySelectorAll('body > .p-dropdown-panel'),
      );
      const panel = panels[panels.length - 1] as HTMLElement;

      if (panel) {
        const panelHeight = panel.offsetHeight || 300; // Altezza del panel

        // Se c'è meno spazio sotto che sopra, o se il panel finirebbe fuori dalla viewport, aprilo verso l'alto
        if (spaceBelow < panelHeight && spaceAbove > spaceBelow) {
          const newTop = dropdownRect.top - panelHeight - 5; // 5px di margine

          // Applica lo stile per posizionare il panel sopra il dropdown
          panel.style.top = `${Math.max(10, newTop)}px`;
          panel.style.bottom = 'auto';
          panel.style.transform = 'translateY(-100%)';
          panel.style.position = 'fixed';
        }
      }
    }, 10);
  }

  /**
   * Gestisce l'apertura del dropdown del tag per forzare l'apertura verso l'alto
   * Stessa logica del dropdown della categoria
   */
  onTagDropdownShow(event: any): void {
    // Aspetta che il DOM si aggiorni e che PrimeNG posizioni il panel
    setTimeout(() => {
      const dropdown = document.getElementById('tag-select');
      if (!dropdown) return;

      const dropdownRect = dropdown.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - dropdownRect.bottom;
      const spaceAbove = dropdownRect.top;

      // Trova il panel del dropdown (potrebbe essere l'ultimo creato)
      const panels = Array.from(
        document.querySelectorAll('body > .p-dropdown-panel'),
      );
      const panel = panels[panels.length - 1] as HTMLElement;

      if (panel) {
        const panelHeight = panel.offsetHeight || 300; // Altezza del panel

        // Se c'è meno spazio sotto che sopra, o se il panel finirebbe fuori dalla viewport, aprilo verso l'alto
        if (spaceBelow < panelHeight && spaceAbove > spaceBelow) {
          const newTop = dropdownRect.top - panelHeight - 5; // 5px di margine

          // Applica lo stile per posizionare il panel sopra il dropdown
          panel.style.top = `${Math.max(10, newTop)}px`;
          panel.style.bottom = 'auto';
          panel.style.transform = 'translateY(-100%)';
          panel.style.position = 'fixed';
        }
      }
    }, 10);
  }

  /**
   * Ottiene tutte le categorie disponibili per il dropdown
   */
  getAvailableCategoriesForDropdown(): Array<{
    label: string;
    value: string;
    icon?: string;
  }> {
    const categories: Array<{ label: string; value: string; icon?: string }> =
      [];

    Object.keys(this.tagsByCategory).forEach((catCode) => {
      const tags = this.tagsByCategory[catCode];
      if (tags && tags.length > 0) {
        const categoryName = tags[0].categoriaTagNome || catCode;
        categories.push({
          label: categoryName.replace(/_/g, ' '),
          value: catCode,
          icon: undefined,
        });
      }
    });

    return categories.sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Ottiene i tag disponibili per il dropdown in formato dropdown options
   */
  getAvailableTagsForDropdown(): Array<{
    label: string;
    value: TagModel;
    disabled?: boolean;
  }> {
    if (!this.selectedCategoryForNewTag) {
      return [];
    }

    const allTags = this.tagsByCategory[this.selectedCategoryForNewTag] || [];

    return allTags.map((tag) => ({
      label: tag.codice.replace(/_/g, ' '),
      value: tag,
      disabled: this.isTagAlreadyPresent(tag),
    }));
  }

  /**
   * Ottiene il nome visualizzato di un tag dal modello
   */
  getTagModelDisplayName(tag: TagModel): string {
    return tag.codice.replace(/_/g, ' ');
  }

  /**
   * Verifica se un tag è già presente nella referenza
   */
  isTagAlreadyPresent(tag: TagModel): boolean {
    if (!this.selectedReferenzaForTags || !this.selectedReferenzaForTags.tag) {
      return false;
    }
    const tagString = `${tag.categoriaTagCodice}#${tag.codice}`;
    return this.selectedReferenzaForTags.tag.includes(tagString);
  }

  /**
   * Conferma l'eliminazione di un tag dalla referenza
   */
  confirmDeleteTagFromReferenza(tag: string): void {
    if (!this.selectedReferenzaForTags) return;

    const displayName = this.getTagDisplayName(tag);
    const categoryName = this.getTagCategoryName(tag);

    this.confirmationService.confirm({
      message: `
        <div class="delete-confirm-message">
          <p class="confirm-text">Sei sicuro di voler rimuovere questo tag dalla referenza?</p>
          <div class="tag-details">
            <div class="detail-row">
              <span class="detail-label">Categoria:</span>
              <span class="detail-value">${categoryName}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Tag:</span>
              <span class="detail-value">${displayName}</span>
            </div>
          </div>
        </div>
      `,
      header: 'Conferma Rimozione Tag',
      icon: 'none',
      acceptLabel: 'Rimuovi',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.deleteTagFromReferenza(tag);
      },
    });
  }

  /**
   * Elimina un tag dalla referenza
   */
  async deleteTagFromReferenza(tagToRemove: string): Promise<void> {
    if (!this.selectedReferenzaForTags || !this.selectedReferenzaForTags.id) {
      this.logger.error(
        '[HOME] Impossibile rimuovere tag: referenza non valida',
      );
      return;
    }

    const ref = this.selectedReferenzaForTags;

    try {
      // Rimuovi il tag dall'array
      const currentTags = ref.tag || [];
      const updatedTags = currentTags.filter((t) => t !== tagToRemove);

      // Converti i tag nel formato API
      const tagRiferimenti = updatedTags.map((tag) => {
        const [codiceCategoria, codiceTag] = tag.split('#');
        return { codiceCategoria, codiceTag };
      });

      // Costruisci il DTO in modo esplicito, mappando solo i campi necessari
      const referenzaDto: ReferenzaModel = {
        id: ref.id,
        codiceCommessa:
          ref.codiceCommerciale !== null && ref.codiceCommerciale !== undefined
            ? typeof ref.codiceCommerciale === 'number'
              ? ref.codiceCommerciale.toString()
              : ref.codiceCommerciale
            : null,
        codiceCertificato:
          ref.codiceCertificato !== null && ref.codiceCertificato !== undefined
            ? (ref.codiceCertificato as number)
            : null,
        projectManager: ref.projectManager || null,
        cliente: ref.cliente || null,
        titoloBreve: ref.titoloBreve,
        titoloEsteso: ref.titoloEsteso || null,
        tempistiche: ref.tempistiche || null,
        dataFine: ref.dataFine || null,
        rtp: ref.rtp || false,
        capogruppo: ref.capogruppo || false,
        quotaCooprogetti:
          ref.quotaCooprogetti !== null && ref.quotaCooprogetti !== undefined
            ? ref.quotaCooprogetti
            : null,
        inAq: ref.inAq || false,
        bim: ref.bim || false,
        valoreDimensione: ref.valoreDimensione || null,
        importoLavori:
          ref.importoLavori !== null && ref.importoLavori !== undefined
            ? ref.importoLavori
            : null,
        importoServizio:
          ref.importoServizio !== null && ref.importoServizio !== undefined
            ? ref.importoServizio
            : null,
        categorieServizio: ref.categorieServizio || null,
        tag: tagRiferimenti.length > 0 ? tagRiferimenti : null,
        dataCreazione: ref.dataCreazione || null,
        dataModifica: new Date().toISOString(),
      };

      await firstValueFrom(
        this.apiService.updateReferenza(ref.id, referenzaDto),
      );

      this.logger.info('[HOME] Tag rimosso con successo dalla referenza');
      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Tag rimosso con successo',
        life: 3000,
      });

      // Aggiorna la referenza locale (Firestore si aggiornerà automaticamente)
      if (this.selectedReferenzaForTags) {
        this.selectedReferenzaForTags.tag = updatedTags;
      }
    } catch (error) {
      this.logger.error('[HOME] Errore durante la rimozione del tag:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Errore durante la rimozione del tag',
        life: 5000,
      });
    }
  }

  /**
   * Aggiunge un tag alla referenza
   */
  async addTagToReferenza(tag: TagModel): Promise<void> {
    if (!this.selectedReferenzaForTags || !this.selectedReferenzaForTags.id) {
      this.logger.error(
        '[HOME] Impossibile aggiungere tag: referenza non valida',
      );
      return;
    }

    if (!tag || !tag.categoriaTagCodice || !tag.codice) {
      this.logger.error('[HOME] Tag non valido:', tag);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Tag non valido',
        life: 3000,
      });
      return;
    }

    const ref = this.selectedReferenzaForTags;

    // Verifica se il tag è già presente
    const tagString = `${tag.categoriaTagCodice}#${tag.codice}`;
    if (this.isTagAlreadyPresent(tag)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Questo tag è già presente nella referenza',
        life: 3000,
      });
      return;
    }

    try {
      // Aggiungi il tag all'array
      const currentTags = ref.tag || [];
      const updatedTags = [...currentTags, tagString];

      this.logger.debug('[HOME] Tag da aggiungere:', {
        tag,
        tagString,
        currentTags,
        updatedTags,
      });

      // Converti i tag nel formato API
      const tagRiferimenti = updatedTags
        .map((t) => {
          const parts = t.split('#');
          if (parts.length !== 2) {
            this.logger.error('[HOME] Formato tag non valido:', t);
            return null;
          }
          return { codiceCategoria: parts[0], codiceTag: parts[1] };
        })
        .filter(
          (t): t is { codiceCategoria: string; codiceTag: string } =>
            t !== null,
        );

      this.logger.debug('[HOME] Tag convertiti per API:', tagRiferimenti);

      if (tagRiferimenti.length === 0) {
        this.logger.error('[HOME] Nessun tag valido da inviare');
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore nella conversione dei tag',
          life: 3000,
        });
        return;
      }

      // Costruisci il DTO in modo esplicito, mappando solo i campi necessari
      const referenzaDto: ReferenzaModel = {
        id: ref.id,
        codiceCommessa:
          ref.codiceCommerciale !== null && ref.codiceCommerciale !== undefined
            ? typeof ref.codiceCommerciale === 'number'
              ? ref.codiceCommerciale.toString()
              : ref.codiceCommerciale
            : null,
        codiceCertificato:
          ref.codiceCertificato !== null && ref.codiceCertificato !== undefined
            ? (ref.codiceCertificato as number)
            : null,
        projectManager: ref.projectManager || null,
        cliente: ref.cliente || null,
        titoloBreve: ref.titoloBreve,
        titoloEsteso: ref.titoloEsteso || null,
        tempistiche: ref.tempistiche || null,
        dataFine: ref.dataFine || null,
        rtp: ref.rtp || false,
        capogruppo: ref.capogruppo || false,
        quotaCooprogetti:
          ref.quotaCooprogetti !== null && ref.quotaCooprogetti !== undefined
            ? ref.quotaCooprogetti
            : null,
        inAq: ref.inAq || false,
        bim: ref.bim || false,
        valoreDimensione: ref.valoreDimensione || null,
        importoLavori:
          ref.importoLavori !== null && ref.importoLavori !== undefined
            ? ref.importoLavori
            : null,
        importoServizio:
          ref.importoServizio !== null && ref.importoServizio !== undefined
            ? ref.importoServizio
            : null,
        categorieServizio: ref.categorieServizio || null,
        tag: tagRiferimenti.length > 0 ? tagRiferimenti : null,
        dataCreazione: ref.dataCreazione || null,
        dataModifica: new Date().toISOString(),
      };

      this.logger.debug('[HOME] DTO referenza per aggiunta tag:', referenzaDto);

      await firstValueFrom(
        this.apiService.updateReferenza(ref.id, referenzaDto),
      );

      this.logger.info('[HOME] Tag aggiunto con successo alla referenza');
      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Tag aggiunto con successo',
        life: 3000,
      });

      // Aggiorna la referenza locale (Firestore si aggiornerà automaticamente)
      if (this.selectedReferenzaForTags) {
        this.selectedReferenzaForTags.tag = updatedTags;
      }

      // Reset del form di aggiunta
      this.selectedTagForNewTag = null;
      // Non resettiamo la categoria così l'utente può aggiungere più tag della stessa categoria
    } catch (error) {
      this.logger.error("[HOME] Errore durante l'aggiunta del tag:", error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: "Errore durante l'aggiunta del tag",
        life: 5000,
      });
    }
  }

  /**
   * Raggruppa i tag di una referenza per gruppo di categorie
   */
  getGroupedTagsForReferenza(ref: ReferenzaFirestore): Array<{
    groupName: string;
    groupLabel: string;
    groupIcon: string;
    tags: string[];
  }> {
    if (!ref.tag || ref.tag.length === 0) {
      return [];
    }

    return this.groupedCategories
      .map((group) => {
        const tags = this.getTagsByGroup(ref, group.groupName);
        return {
          groupName: group.groupName,
          groupLabel: group.groupLabel,
          groupIcon: group.groupIcon,
          tags,
        };
      })
      .filter((group) => group.tags.length > 0);
  }

  /**
   * Ottiene il nome della categoria di un tag
   * Formato tag: "CATEGORIA#CODICE" -> restituisce il nome della categoria
   * Sostituisce gli underscore con spazi per la visualizzazione
   */
  getTagCategoryName(tag: string): string {
    const [categoryCode] = tag.split('#');
    if (!categoryCode) return '';

    // Cerca il nome della categoria nei tag caricati
    for (const catCode of Object.keys(this.tagsByCategory)) {
      if (catCode === categoryCode && this.tagsByCategory[catCode].length > 0) {
        const categoryName =
          this.tagsByCategory[catCode][0].categoriaTagNome || categoryCode;
        return categoryName.replace(/_/g, ' ');
      }
    }

    return categoryCode.replace(/_/g, ' ');
  }

  /**
   * Verifica se un gruppo ha tag disponibili nelle referenze
   */
  hasTagsInGroup(groupName: string): boolean {
    return this.referenze.some((ref) => {
      const tags = this.getTagsByGroup(ref, groupName);
      return tags.length > 0;
    });
  }

  /**
   * Apre il wizard per modificare una referenza
   */
  openEditReferenzaWizard(referenza: ReferenzaFirestore): void {
    this.referenzaToEdit = referenza;
    this.showNuovaReferenzaWizard = true;
  }

  /**
   * Conferma l'eliminazione di una referenza
   */
  confirmDeleteReferenza(referenza: ReferenzaFirestore): void {
    this.confirmationService.confirm({
      message: `
        <div class="delete-confirm-message">
          <p class="confirm-text">Sei sicuro di voler eliminare questa referenza?</p>
          <div class="tag-details">
            <div class="detail-row">
              <span class="detail-label">Titolo:</span>
              <span class="detail-value">${referenza.titoloBreve}</span>
            </div>
            ${
              referenza.cliente
                ? `
            <div class="detail-row">
              <span class="detail-label">Cliente:</span>
              <span class="detail-value">${referenza.cliente}</span>
            </div>
            `
                : ''
            }
            ${
              referenza.codiceCommerciale
                ? `
            <div class="detail-row">
              <span class="detail-label">Codice Commessa:</span>
              <span class="detail-value code-value">${referenza.codiceCommerciale}</span>
            </div>
            `
                : ''
            }
          </div>
          <p class="warning-text">Questa azione non può essere annullata.</p>
        </div>
      `,
      header: 'Conferma Eliminazione',
      icon: 'none',
      acceptLabel: 'Elimina',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.deleteReferenza(referenza);
      },
    });
  }

  /**
   * Ottiene la classe CSS per lo stato della data (passata, presente, futura)
   */
  getDateStatusClass(dataFine: string | null | undefined): string {
    if (!dataFine) return 'date-neutral';

    const data = new Date(dataFine);
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    data.setHours(0, 0, 0, 0);

    const diffTime = data.getTime() - oggi.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return 'date-expired'; // Data passata
    } else if (diffDays === 0) {
      return 'date-today'; // Oggi
    } else if (diffDays <= 30) {
      return 'date-soon'; // Prossimi 30 giorni
    } else {
      return 'date-future'; // Futuro
    }
  }

  /**
   * Ottiene l'icona per la data in base allo stato
   */
  getDateIcon(dataFine: string | null | undefined): string {
    if (!dataFine) return 'pi-calendar';

    const data = new Date(dataFine);
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    data.setHours(0, 0, 0, 0);

    const diffTime = data.getTime() - oggi.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return 'pi-exclamation-triangle'; // Data passata
    } else if (diffDays === 0) {
      return 'pi-clock'; // Oggi
    } else if (diffDays <= 30) {
      return 'pi-calendar-times'; // Prossimi 30 giorni
    } else {
      return 'pi-calendar'; // Futuro
    }
  }

  /**
   * Formatta un importo in formato italiano (punti per migliaia, virgola per decimali)
   */
  formatImporto(value: number | null | undefined): string {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /**
   * Ottiene tutti i certificati (gestisce retrocompatibilità con codiceCertificato singolo)
   */
  getCertificati(ref: ReferenzaFirestore): string[] {
    const certificati: string[] = [];

    // Aggiungi i nuovi certificati (array)
    if (ref.codiciCertificati && ref.codiciCertificati.length > 0) {
      certificati.push(...ref.codiciCertificati);
    }

    // Aggiungi il vecchio certificato singolo se presente e non già incluso
    if (ref.codiceCertificato) {
      const certString = ref.codiceCertificato.toString();
      if (!certificati.includes(certString)) {
        certificati.push(certString);
      }
    }

    return certificati;
  }

  /**
   * Formatta la quota cooprogetti come percentuale
   */
  formatQuota(value: number | null | undefined): string {
    if (value === null || value === undefined) return '';
    // Se il valore è già in percentuale (0-100), usa direttamente, altrimenti moltiplica per 100
    const percentuale = value <= 1 ? value * 100 : value;
    // Mostra decimali solo se diversi da 0
    const hasDecimals = percentuale % 1 !== 0;
    return (
      new Intl.NumberFormat('it-IT', {
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(percentuale) + '%'
    );
  }

  /**
   * Genera il testo del tooltip per la colonna Quota
   * Mostra i valori di CAPOGRUPPO e RTP se presenti
   */
  getQuotaTooltip(ref: ReferenzaFirestore): string {
    const parts: string[] = [];

    if (ref.capogruppo === true) {
      parts.push('Capogruppo: Sì');
    }

    if (ref.rtp === true) {
      parts.push('RTP: Sì');
    }

    return parts.length > 0 ? parts.join('\n') : '';
  }

  /**
   * Verifica se il tooltip della quota deve essere mostrato
   */
  hasQuotaTooltip(ref: ReferenzaFirestore): boolean {
    return ref.capogruppo === true || ref.rtp === true;
  }

  /**
   * Elimina una referenza
   */
  async deleteReferenza(referenza: ReferenzaFirestore): Promise<void> {
    try {
      await firstValueFrom(this.apiService.deleteReferenza(referenza.id));

      /* this.logger.info(
        '[HOME] Referenza eliminata con successo:',
        referenza.id,
      ); */

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Referenza eliminata con successo',
        life: 3000,
      });
    } catch (error) {
      this.logger.error(
        "[HOME] Errore durante l'eliminazione della referenza:",
        error,
      );

      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: "Errore durante l'eliminazione della referenza",
        life: 5000,
      });
    }
  }
}
